import validator from "validator";

const EMAIL_FONT_FAMILY = "Calibri, Arial, sans-serif";
const EMAIL_FONT_SIZE = "14px";
const EMAIL_LINE_HEIGHT = "1.4";
const EMAIL_TEXT_COLOR = "#000000";

class EmailUtils {
  private static normalizeFontFamily(value: string): string {
    const trimmed = value.trim();
    if (!trimmed || /\binherit\b/i.test(trimmed)) return EMAIL_FONT_FAMILY;
    return trimmed;
  }

  private static setStyleIfMissing(el: HTMLElement, property: string, value: string) {
    if (el.style.getPropertyValue(property).trim()) return;
    el.style.setProperty(property, value);
  }

  private static ensureEmailTypography(doc: Document) {
    const bodyStyle = doc.body.style;
    const fontFamily = EmailUtils.normalizeFontFamily(
      bodyStyle.fontFamily || EMAIL_FONT_FAMILY,
    );
    const fontSize = bodyStyle.fontSize || EMAIL_FONT_SIZE;
    const lineHeight = bodyStyle.lineHeight || EMAIL_LINE_HEIGHT;
    const color = bodyStyle.color || EMAIL_TEXT_COLOR;

    bodyStyle.fontFamily = fontFamily;
    bodyStyle.fontSize = fontSize;
    bodyStyle.lineHeight = lineHeight;
    bodyStyle.color = color;

    doc
      .querySelectorAll<HTMLElement>(
        "a, div, em, h1, h2, h3, h4, h5, h6, li, p, span, strong, table, tbody, td, th, thead, tr",
      )
      .forEach((el) => {
        if (el.style.fontFamily) {
          el.style.fontFamily = EmailUtils.normalizeFontFamily(el.style.fontFamily);
        } else {
          EmailUtils.setStyleIfMissing(el, "font-family", fontFamily);
        }

        EmailUtils.setStyleIfMissing(el, "font-size", fontSize);
        EmailUtils.setStyleIfMissing(el, "line-height", lineHeight);
        EmailUtils.setStyleIfMissing(el, "color", color);
      });
  }

  static parseRecipientAddresses(value?: string | null): string[] {
    if (!value) return [];

    const recipients: string[] = [];
    let current = "";
    let quoteChar: '"' | "'" | null = null;
    let angleDepth = 0;

    const normalizeAddress = (raw: string) => {
      const trimmed = raw.trim();
      const angleMatch = trimmed.match(/<([^<>]+)>/);
      const address = (angleMatch?.[1] ?? trimmed).replace(/^mailto:/i, "").trim();
      return address.replace(/^["']|["']$/g, "").trim();
    };

    const nextTopLevelSegment = (startIndex: number) => {
      let nextQuoteChar: '"' | "'" | null = null;
      let nextAngleDepth = 0;
      let segment = "";

      for (let i = startIndex; i < value.length; i += 1) {
        const char = value[i];

        if ((char === '"' || char === "'") && nextAngleDepth === 0) {
          nextQuoteChar = nextQuoteChar === char ? null : char;
        } else if (!nextQuoteChar) {
          if (char === "<") nextAngleDepth += 1;
          if (char === ">") nextAngleDepth = Math.max(0, nextAngleDepth - 1);
          if ((char === "," || char === ";") && nextAngleDepth === 0) break;
        }

        segment += char;
      }

      return segment;
    };

    const isSeparator = (char: string, index: number) => {
      if (quoteChar || angleDepth > 0) return false;
      if (char === ";") return true;
      if (char !== ",") return false;

      const currentTrimmed = current.trim();
      if (!currentTrimmed || currentTrimmed.includes("<")) return true;
      if (EmailUtils.isValidEmail(currentTrimmed)) return true;

      const nextSegment = nextTopLevelSegment(index + 1);
      return !(nextSegment.includes("<") && nextSegment.includes(">"));
    };

    const pushCurrent = () => {
      const address = normalizeAddress(current);
      if (address) recipients.push(address);
      current = "";
    };

    for (let i = 0; i < value.length; i += 1) {
      const char = value[i];

      if ((char === '"' || char === "'") && angleDepth === 0) {
        quoteChar = quoteChar === char ? null : char;
      } else if (!quoteChar) {
        if (char === "<") angleDepth += 1;
        if (char === ">") angleDepth = Math.max(0, angleDepth - 1);
      }

      if (isSeparator(char, i)) {
        pushCurrent();
        continue;
      }

      current += char;
    }

    pushCurrent();

    return recipients;
  }

  /**
   * Inlines CSS rules from every `<style>` block in the document onto
   * matching elements as inline styles, then returns `doc.body.innerHTML`.
   *
   * This is needed for the port-call notification send path: the original
   * template uses class-based rules (.completed, .success, .failed) that
   * Lexical strips when it re-serialises the HTML.  Inlining ensures the
   * styling survives the Lexical round-trip and arrives intact in Outlook.
   *
   * Only handles flat selectors (element, .class, element.class,
   * element[attr]).  Media queries and pseudo-selectors are skipped safely.
   */
  static inlineHtmlStyles(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const rules: Array<{ selector: string; declarations: string }> = [];

    doc.querySelectorAll("style").forEach((styleEl) => {
      const cssText = styleEl.textContent ?? "";
      // Match selector { declarations } — skip @-rules and nested blocks
      const ruleRegex = /([^@{}][^{}]*)\{([^{}]+)\}/g;
      let match: RegExpExecArray | null;
      while ((match = ruleRegex.exec(cssText)) !== null) {
        const selector = match[1].trim();
        const declarations = match[2].trim();
        if (selector && declarations) {
          rules.push({ selector, declarations });
        }
      }
    });

    rules.forEach(({ selector, declarations }) => {
      try {
        doc.querySelectorAll<HTMLElement>(selector).forEach((el) => {
          const existing = el.style.cssText;
          el.style.cssText = existing ? `${existing}; ${declarations}` : declarations;
        });
      } catch {
        // Skip selectors that the browser cannot parse (e.g. pseudo-elements)
      }
    });

    // Remove <style> tags — styles are now inline
    EmailUtils.ensureEmailTypography(doc);
    doc.querySelectorAll("style").forEach((el) => el.remove());

    return doc.body.innerHTML;
  }

  /**
   * Stamps `margin:0; padding:0` onto every `<p>` that has no explicit margin.
   *
   * Lexical serialises paragraph breaks as `<p>` elements. Outlook (and most
   * email clients) apply a default bottom margin (~16 px) to unstyled `<p>`
   * tags. When a user edits a port-call email and presses Enter, what was a
   * single `<p>` with `<br>` separators becomes multiple `<p>` elements — each
   * accumulating that margin and producing extra blank lines in the sent email.
   *
   * Only paragraphs that carry no inline margin are touched, so intentionally
   * spaced blocks elsewhere in the email template are left intact.
   */
  static normalizeEmailParagraphs(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    doc.querySelectorAll<HTMLElement>("p").forEach((el) => {
      if (el.style.margin || el.style.marginTop || el.style.marginBottom) return;
      const existing = el.style.cssText.trim();
      el.style.cssText = existing
        ? `${existing}; margin: 0; padding: 0;`
        : "margin: 0; padding: 0;";
    });

    EmailUtils.ensureEmailTypography(doc);

    return doc.body.innerHTML;
  }

  static isValidEmail(email: string) {
    try {
      // Trim the email first
      const trimmedEmail = email?.trim() || "";
      
      // Empty after trim = valid (optional email fields allowed)
      if (trimmedEmail === "") return true;
      
      // Must start with alphanumeric
      if (!/^[a-zA-Z0-9]/.test(trimmedEmail)) return false;
      
      return validator.isEmail(trimmedEmail, {
        allow_display_name: false,
        require_tld: true,
        allow_utf8_local_part: false,
        require_display_name: false,
        allow_ip_domain: false,
        domain_specific_validation: true,
      });
    } catch (error) {
      console.log(error);
      return false;
    }
  }
}

export function getEmailWarningState(email: string | null | undefined): {
  isMissing: boolean;
  isInvalid: boolean;
  hasEmailWarning: boolean;
  tooltip: string;
} {
  const isMissing = !email || email.trim() === "";
  const isInvalid = !isMissing && !EmailUtils.isValidEmail(email);
  return {
    isMissing,
    isInvalid,
    hasEmailWarning: isMissing || isInvalid,
    tooltip: isMissing ? "Missing email address" : isInvalid ? "Invalid email format" : "",
  };
}

export default EmailUtils;
